import { Icon } from '../../../symbols';
import style from './item.module.css';
import {
  ComponentProps,
  forwardRef,
  PropsWithChildren,
  useMemo,
  useRef,
} from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Text } from '../../../typography';
import { Profile } from '../../types';
import { ClassName, cn } from '../../../../utils';
import { getIcon } from '../../utils/get-icon';

type ProfileItemProps = {
  profile: Profile;
  current?: boolean;
};

type ItemProps = PropsWithChildren<{
  icon?: ComponentProps<typeof Icon>['name'];
  asChild?: boolean;
  className?: ClassName;
}>;

export const ProfileNavItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ icon, children, asChild, className }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp className={cn(style.item, className)} ref={ref} role="menuitem">
        {icon && <Icon name={icon} className={style.icon} />}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);

export const ProfileSelectItem = forwardRef<HTMLDivElement, ProfileItemProps>(
  ({ profile, current }, outerRef) => {
    const innerRef = useRef<HTMLDivElement>(null);

    if (typeof outerRef === 'function') {
      outerRef(innerRef.current);
    } else if (outerRef) {
      outerRef.current = innerRef.current;
    }

    const isFirst = useMemo(
      () =>
        innerRef.current?.parentElement?.firstElementChild === innerRef.current,
      [innerRef.current],
    );

    const isLast = useMemo(
      () =>
        innerRef.current?.parentElement?.lastElementChild === innerRef.current,
      [innerRef.current],
    );
    return (
      <ProfileNavItem
        icon={getIcon(profile.profileType)}
        className={cn(isFirst && style.first, isLast && style.last)}
        ref={innerRef}
        asChild
      >
        <button name="profile" value={profile.id} type="submit">
          <div className={style.label}>
            <div className={style['small-text']}>{profile.label}</div>

            <Text variant={current ? 'body-bold' : 'body'} asChild>
              <div>{profile.name}</div>
            </Text>
          </div>
          {current && <Icon name="checkmark" className={style.checkmark} />}
        </button>
      </ProfileNavItem>
    );
  },
);
